import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface Post {
  id: number;
  slug: string;
  title: { rendered: string };
  excerpt: { rendered: string };
  link: string;
  _embedded?: any;
}

export default function BlogSlideshow() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const duration = 5000;
  const updateInterval = 50;

  // Fetch posts
  useEffect(() => {
    fetch(
      "https://blog.findnigeriaproperties.com/wp-json/wp/v2/posts?per_page=3&_embed",
    )
      .then((res) => res.json())
      .then((data) => setPosts(data))
      .catch((err) => console.error(err));
  }, []);

  // Progress animation
  useEffect(() => {
    if (!posts.length) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentIndex((current) => (current + 1) % posts.length);
          return 0;
        }
        return prev + (updateInterval / duration) * 100;
      });
    }, updateInterval);

    return () => clearInterval(interval);
  }, [posts]);

  const selectPost = (index: number) => {
    setCurrentIndex(index);
    setProgress(0);
  };

  const getFeaturedImage = (post: Post) => {
    return (
      post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
      "/placeholder.jpg"
    );
  };

  if (!posts.length) return null;

  return (
    <div className="flex flex-col items-center mt-16 lg:mt-36">
      <div className="flex flex-col items-center gap-4 lg:w-[471px]">
        <h1 className="text-main-900 text-center font-bold text-4xl">
          Latest From Our Blog
        </h1>
        <p className="text-gray-500 text-center font-normal text-base">
          Property insights, investment tips & market updates
        </p>
      </div>

      <div className="flex flex-col items-center lg:w-[638px] min-h-[220px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-center mt-14"
          >
            <h3
              className="text-main-900 font-bold text-xl"
              dangerouslySetInnerHTML={{
                __html: posts[currentIndex].title.rendered,
              }}
            />

            <div
              className="text-gray-500 mt-4 text-base"
              dangerouslySetInnerHTML={{
                __html: posts[currentIndex].excerpt.rendered,
              }}
            />

            <a
              href={posts[currentIndex].link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6 text-[#2D96A7] font-semibold"
            >
              Read Full Article →
            </a>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex gap-4 mt-8">
        {posts.map((post, index) => (
          <button key={post.id} onClick={() => selectPost(index)}>
            <img
              src={getFeaturedImage(post)}
              className="w-16 h-16 object-cover rounded-full border-2 border-gray-200"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
